/**
 * Descriptor de Transação financeira — o registro histórico e relacional de negócios entre pessoas
 * e empresas (compra e venda, doação, empréstimo…). As partes (comprador/vendedor/doador…) e os
 * bens (veículo/imóvel) ficam em abas próprias (painéis custom), e a correlação aparece no dossiê e
 * no grafo. Ingestão por documento (contrato/nota/recibo/DUT) entra pela aba Documentos.
 */
import { ArrowLeftRight } from 'lucide-react';
import { createElement } from 'react';
import { PainelBensTransacao } from '@/components/crud/paineis/PainelBensTransacao';
import { PainelPartesTransacao } from '@/components/crud/paineis/PainelPartesTransacao';
import type { TransacaoCad } from '@/lib/api';
import { definirRecurso } from '@/lib/recursos/tipos';

const NATUREZA = [
  { valor: 'compra_venda', rotulo: 'Compra e venda' },
  { valor: 'doacao', rotulo: 'Doação' },
  { valor: 'emprestimo', rotulo: 'Empréstimo' },
  { valor: 'aluguel', rotulo: 'Aluguel' },
  { valor: 'prestacao_servico', rotulo: 'Prestação de serviço' },
  { valor: 'investimento', rotulo: 'Investimento' },
  { valor: 'heranca', rotulo: 'Herança' },
  { valor: 'permuta', rotulo: 'Permuta' },
  { valor: 'outro', rotulo: 'Outro' },
];

export const transacao = definirRecurso<TransacaoCad>({
  chave: 'transacoes',
  endpoint: '/cadastro/transacoes',
  ownerTipo: 'transacao',
  titulo: 'Transações',
  tituloSingular: 'transação',
  descricao: 'Transações financeiras — o registro histórico e relacional entre partes.',
  icone: ArrowLeftRight,
  capacidades: { podeEditar: true, podeExcluir: true },
  buscaPlaceholder: 'Buscar por descrição ou natureza',
  rotuloItem: (t) => t.descricao || NATUREZA.find((n) => n.valor === t.natureza)?.rotulo || 'Transação',

  colunas: [
    {
      chave: 'natureza',
      rotulo: 'Natureza',
      render: (t) => NATUREZA.find((n) => n.valor === t.natureza)?.rotulo ?? t.natureza,
    },
    { chave: 'descricao', rotulo: 'Descrição', ocultarEm: 'sm' },
    {
      chave: 'valor',
      rotulo: 'Valor',
      alinhamento: 'direita',
      mono: true,
      render: (t) =>
        t.valor != null
          ? Number(t.valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
          : '—',
    },
    { chave: 'data', rotulo: 'Data', formato: 'data', mono: true, ocultarEm: 'md' },
  ],

  camposCriar: [
    { chave: 'natureza', rotulo: 'Natureza', tipo: 'select', opcoes: NATUREZA, obrigatorio: true },
    { chave: 'descricao', rotulo: 'Descrição', tipo: 'texto' },
    { chave: 'valor', rotulo: 'Valor', tipo: 'dinheiro' },
    { chave: 'data', rotulo: 'Data', tipo: 'data' },
  ],

  // Cadastro por documento: anexa um contrato, nota fiscal ou recibo; a IA lê natureza, valor, data
  // e as partes (por CPF/CNPJ) e correlaciona — ou insere à mão. O DUT do veículo também é negócio.
  importar: {
    rotulo: 'Importar documento',
    selecionavel: true,
    corpoMinimo: { natureza: 'outro' },
    descricao:
      'Anexe um contrato, nota fiscal ou recibo. A IA lê a natureza, o valor, a data e as partes (por CPF/CNPJ) e correlaciona; você revisa antes de gravar.',
  },

  gruposEdicao: [
    {
      id: 'dados',
      titulo: 'Dados da Transação',
      campos: [
        { chave: 'natureza', rotulo: 'Natureza', tipo: 'select', opcoes: NATUREZA },
        { chave: 'data', rotulo: 'Data', tipo: 'data' },
        { chave: 'descricao', rotulo: 'Descrição', tipo: 'texto', colSpan: 2 },
        { chave: 'valor', rotulo: 'Valor', tipo: 'dinheiro' },
        { chave: 'moeda', rotulo: 'Moeda', tipo: 'texto' },
        { chave: 'situacao', rotulo: 'Situação', tipo: 'texto' },
        { chave: 'fonte', rotulo: 'Fonte do dado', tipo: 'texto' },
        { chave: 'observacoes', rotulo: 'Observações', tipo: 'texto-longo', colSpan: 2 },
      ],
    },
    {
      id: 'sistema',
      titulo: 'Situação',
      campos: [
        { chave: 'ativo', rotulo: 'Ativo', tipo: 'booleano' },
        { chave: 'created_at', rotulo: 'Criado em', tipo: 'leitura' },
        { chave: 'updated_at', rotulo: 'Atualizado em', tipo: 'leitura' },
      ],
    },
  ],

  abas: [
    { id: 'dados', rotulo: 'Dados', grupos: ['dados', 'sistema'] },
    {
      id: 'partes',
      rotulo: 'Partes',
      paineis: [
        {
          tipo: 'custom',
          id: 'partes-transacao',
          titulo: 'Partes',
          render: ({ item }) => createElement(PainelPartesTransacao, { item }),
        },
      ],
    },
    {
      id: 'bens',
      rotulo: 'Bens',
      paineis: [
        {
          tipo: 'custom',
          id: 'bens-transacao',
          titulo: 'Bens',
          render: ({ item }) => createElement(PainelBensTransacao, { item }),
        },
      ],
    },
    { id: 'documentos', rotulo: 'Documentos', paineis: [{ tipo: 'documentos' }] },
    { id: 'dossie', rotulo: 'Dossiê', paineis: [{ tipo: 'dossie' }] },
    { id: 'grafo', rotulo: 'Grafo', paineis: [{ tipo: 'grafo' }] },
  ],
});
